import * as React from 'react'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Save, X, Plus, Check, ChevronDown, ChevronUp } from 'lucide-react'
import {
  useAgentTools,
  useListConfigs,
  useGetConfig,
  useUpdateConfig,
  type AgentTool,
  type OrgConfig,
  type SuggestedPrompt,
} from '@/hooks/useConfigAdmin'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect as Select } from '@/components/ui/select'
import { AgentPromptSection } from '@/components/AgentPromptSection'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { useOrganization } from '@clerk/clerk-react'
import davidImage from '@/images/david-bust.webp'
import hannaImage from '@/images/hanna-bust.webp'

const EMPTY_AGENT_TOOLS: AgentTool[] = []

function formatToolRoles(roles: string[]) {
  if (roles.includes('org:member')) return 'All roles'
  return roles
    .map((role) => role.replace('org:', '').replace('_', ' '))
    .join(', ')
}

// Component for managing a list of tables
function TableList({
  label,
  tables,
  onChange,
}: {
  label: string
  tables: string[]
  onChange: (tables: string[]) => void
}) {
  const [newTable, setNewTable] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const addTable = () => {
    if (newTable.trim()) {
      onChange([...tables, newTable.trim()])
      setNewTable('')
      setIsAdding(false)
    }
  }

  const removeTable = (index: number) => {
    onChange(tables.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="space-y-1">
        {tables.map((table, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-sm font-mono bg-gradient-to-r from-[#f5f7fe] to-[#faf9ff] border border-[#e0e7fe] px-2 py-1 rounded flex-1">
              {table}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeTable(index)}
              className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {isAdding ? (
          <div className="flex items-center gap-2">
            <Input
              value={newTable}
              onChange={(e) => setNewTable(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addTable()
                } else if (e.key === 'Escape') {
                  setNewTable('')
                  setIsAdding(false)
                }
              }}
              placeholder="table_name"
              className="flex-1 h-7 font-mono text-sm"
              autoFocus
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addTable}
              className="h-7 w-7 p-0 hover:bg-emerald-50 hover:text-emerald-600"
              title="Add">
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setNewTable('')
                setIsAdding(false)
              }}
              className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
              title="Cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="flex-1"></span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 w-7 p-0 hover:bg-[#e0e7fe] hover:text-[#8e3bd3]"
              title="Add table">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function ToolPermissionList({
  label,
  idPrefix,
  tools,
  enabledTools,
  onChange,
}: {
  label: string
  idPrefix: string
  tools: AgentTool[]
  enabledTools: string[]
  onChange: (tools: string[]) => void
}) {
  const enabled = new Set(enabledTools)

  const toggleTool = (tool: string, checked: boolean) => {
    if (checked) {
      if (enabled.has(tool)) return
      onChange([...enabledTools, tool])
      return
    }
    onChange(enabledTools.filter((name) => name !== tool))
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid gap-2 sm:grid-cols-2">
        {tools.map((tool) => (
          <div key={tool.name} className="flex min-w-0 items-center gap-2">
            <Checkbox
              id={`${idPrefix}-${tool.name}`}
              checked={enabled.has(tool.name)}
              onCheckedChange={(checked) =>
                toggleTool(tool.name, checked === true)
              }
            />
            <div className="min-w-0">
              <Label
                htmlFor={`${idPrefix}-${tool.name}`}
                className="block min-w-0 truncate font-mono text-sm">
                {tool.name}
              </Label>
              <span className="block truncate text-xs text-muted-foreground">
                {formatToolRoles(tool.allowed_roles)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Component for managing a list of org-default holiday dates ('YYYY-MM-DD').
function HolidayList({
  label,
  holidays,
  onChange,
}: {
  label: string
  holidays: string[]
  onChange: (holidays: string[]) => void
}) {
  const [newDate, setNewDate] = useState('')

  const addDate = () => {
    const value = newDate.trim()
    if (!value || holidays.includes(value)) return
    onChange([...holidays, value].toSorted())
    setNewDate('')
  }

  const removeDate = (date: string) => {
    onChange(holidays.filter((d) => d !== date))
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="space-y-1">
        {holidays.map((date) => (
          <div key={date} className="flex items-center gap-2">
            <span className="text-sm font-mono bg-gradient-to-r from-[#f5f7fe] to-[#faf9ff] border border-[#e0e7fe] px-2 py-1 rounded flex-1">
              {date}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeDate(date)}
              className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
              title="Remove date">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addDate()
              }
            }}
            className="flex-1 h-7 font-mono text-sm"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addDate}
            disabled={!newDate.trim()}
            className="h-7 w-7 p-0 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
            title="Add date">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function PromptList({
  label,
  prompts,
  onChange,
}: {
  label: string
  prompts: SuggestedPrompt[]
  onChange: (prompts: SuggestedPrompt[]) => void
}) {
  const [newTitle, setNewTitle] = useState('')
  const [newMessage, setNewMessage] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editMessage, setEditMessage] = useState('')

  const addPrompt = () => {
    const title = newTitle.trim()
    const message = newMessage.trim()
    if (title && message) {
      onChange([...prompts, { title, message }])
      setNewTitle('')
      setNewMessage('')
      setIsAdding(false)
    }
  }

  const cancelEdit = () => {
    setEditingIndex(null)
    setEditTitle('')
    setEditMessage('')
  }

  const startEdit = (index: number) => {
    setIsAdding(false)
    setNewTitle('')
    setNewMessage('')
    setEditingIndex(index)
    setEditTitle(prompts[index]?.title ?? '')
    setEditMessage(prompts[index]?.message ?? '')
  }

  const saveEdit = () => {
    if (editingIndex === null) return
    const title = editTitle.trim()
    const message = editMessage.trim()
    if (!title || !message) return

    onChange(
      prompts.map((p, i) => (i === editingIndex ? { title, message } : p))
    )
    cancelEdit()
  }

  const removePrompt = (index: number) => {
    onChange(prompts.filter((_, i) => i !== index))
    setEditingIndex((current) => {
      if (current === null) return current
      if (current === index) return null
      if (index < current) return current - 1
      return current
    })
  }

  const canAdd = Boolean(newTitle.trim() && newMessage.trim())
  const canSave = Boolean(editTitle.trim() && editMessage.trim())

  useEffect(() => {
    if (editingIndex === null) return
    if (editingIndex < 0 || editingIndex >= prompts.length) {
      cancelEdit()
    }
  }, [editingIndex, prompts.length])

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="space-y-1">
        {prompts.map((prompt, index) => {
          const isEditing = editingIndex === index

          if (isEditing) {
            return (
              <div
                key={`${index}-${prompt.title}`}
                className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Title"
                    className="h-7 text-sm"
                    autoFocus
                  />
                  <Input
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        saveEdit()
                      } else if (e.key === 'Escape') {
                        cancelEdit()
                      }
                    }}
                    placeholder="Message"
                    className="h-7 text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={saveEdit}
                  disabled={!canSave}
                  className="h-7 w-7 p-0 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
                  title="Save">
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
                  title="Cancel">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          }

          return (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono bg-gradient-to-r from-[#f5f7fe] to-[#faf9ff] border border-[#e0e7fe] px-2 py-1 rounded">
                  {prompt.title}
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {prompt.message}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => startEdit(index)}
                className="h-7 px-2 hover:bg-[#e0e7fe] hover:text-[#8e3bd3]"
                title="Edit">
                Edit
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removePrompt(index)}
                className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
                title="Delete">
                <X className="h-4 w-4" />
              </Button>
            </div>
          )
        })}
        {isAdding ? (
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Title"
                className="h-7 text-sm"
                autoFocus
              />
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addPrompt()
                  } else if (e.key === 'Escape') {
                    setNewTitle('')
                    setNewMessage('')
                    setIsAdding(false)
                  }
                }}
                placeholder="Message"
                className="h-7 text-sm"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addPrompt}
              disabled={!canAdd}
              className="h-7 w-7 p-0 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50"
              title="Add">
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                cancelEdit()
                setNewTitle('')
                setNewMessage('')
                setIsAdding(false)
              }}
              className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
              title="Cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="flex-1"></span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                cancelEdit()
                setIsAdding(true)
              }}
              className="h-7 w-7 p-0 hover:bg-[#e0e7fe] hover:text-[#8e3bd3]"
              title="Add prompt">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export function ConfigManager() {
  const [selectedOrgState, setSelectedOrg] = useState<string | null>(null)
  const [formData, setFormData] = useState<OrgConfig | null>(null)
  const [originalFormData, setOriginalFormData] = useState<OrgConfig | null>(
    null
  )
  const [expandedSections, setExpandedSections] = useState<{
    david: boolean
    hanna: boolean
    features: boolean
    orgInstructions: boolean
    calling: boolean
  }>({
    david: false,
    hanna: false,
    features: true,
    orgInstructions: false,
    calling: false,
  })

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  // Careforce global admins manage any org via the selector; global admins of
  // any other org are scoped to that org (no selector, no all-orgs list). This
  // follows the active org, matching the backend's per-request authorization.
  const isGlobalAdmin = useIsGlobalAdmin()
  const { organization } = useOrganization()
  const currentOrgSlug = organization?.slug ?? null
  const canManageAllOrgs = isGlobalAdmin && currentOrgSlug === 'careforce'

  // Careforce global admins pick any org via the selector; other global admins
  // are pinned to their active org.
  const selectedOrg = canManageAllOrgs ? selectedOrgState : currentOrgSlug

  // Queries and mutations
  const { data: configs, isLoading: loadingList } =
    useListConfigs(canManageAllOrgs)
  const { data: agentTools, isLoading: loadingAgentTools } = useAgentTools()
  const { data: configData, isLoading: loadingConfig } =
    useGetConfig(selectedOrg)
  const updateMutation = useUpdateConfig()

  // Load config data when selected org changes
  useEffect(() => {
    if (configData?.config) {
      const cfg = configData.config
      const normalized: OrgConfig = {
        ...cfg,
        agents: {
          ...cfg.agents,
          david: {
            ...cfg.agents.david,
            enabled_tools: cfg.agents.david.enabled_tools ?? [],
          },
          hanna: {
            ...cfg.agents.hanna,
            enabled_tools: cfg.agents.hanna.enabled_tools ?? [],
          },
        },
      }
      setFormData(normalized)
      setOriginalFormData(normalized)
    }
  }, [configData])

  // Check if form data has actually changed from original
  const hasChanges = React.useMemo(() => {
    if (!originalFormData) return false
    return JSON.stringify(formData) !== JSON.stringify(originalFormData)
  }, [formData, originalFormData])

  const handleCancel = () => {
    if (originalFormData) {
      setFormData(originalFormData)
    }
  }

  const handleSave = async () => {
    if (!selectedOrg || !formData) return

    try {
      await updateMutation.mutateAsync({
        orgSlug: selectedOrg,
        config: formData,
      })
      toast.success(`Configuration updated for ${selectedOrg}`)
      // Update original data to match current form data after successful save
      setOriginalFormData(formData)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update configuration'
      )
    }
  }

  if (loadingList) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading organizations...</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Organization Selector */}
      <div className="border-b p-4">
        <Label htmlFor="org-select">Organization</Label>
        {canManageAllOrgs ? (
          <Select
            id="org-select"
            value={selectedOrg || ''}
            onChange={(e) => setSelectedOrg(e.target.value || null)}
            className="mt-2">
            <option value="">Select an organization...</option>
            {configs?.map((config) => (
              <option key={config.org_slug} value={config.org_slug}>
                {config.org_slug}
              </option>
            ))}
          </Select>
        ) : (
          <p className="mt-2 text-sm font-medium">{currentOrgSlug ?? '—'}</p>
        )}
      </div>

      {/* Form Content */}
      {selectedOrg && (
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {loadingConfig ? (
            <p className="text-muted-foreground">Loading configuration...</p>
          ) : (
            <>
              {/* David Agent Section */}
              <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('david')}>
                  <div className="flex items-center gap-3">
                    <span className="bg-[linear-gradient(#cacffe,#b2a3fd)] rounded-[50%] overflow-clip">
                      <img src={davidImage} alt="David" className="w-8 h-8" />
                    </span>
                    <span className="font-semibold text-base text-gray-800">
                      David
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        id="david-enabled"
                        checked={formData?.agents?.david?.enabled || false}
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  agents: {
                                    ...prev.agents,
                                    david: {
                                      ...prev.agents.david,
                                      enabled: e.target.checked,
                                    },
                                  },
                                }
                              : prev
                          )
                        }
                        className="h-4 w-4"
                      />
                      <Label htmlFor="david-enabled" className="text-sm">
                        Enabled
                      </Label>
                    </div>
                    {expandedSections.david ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>
                {expandedSections.david && (
                  <div className="space-y-4 pt-4 border-t border-[#e0e7fe]">
                    <TableList
                      label="Read Allowed Tables"
                      tables={
                        formData?.agents?.david?.read_allowed_tables || []
                      }
                      onChange={(tables) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  david: {
                                    ...prev.agents.david,
                                    read_allowed_tables: tables,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <TableList
                      label="Write Allowed Tables"
                      tables={
                        formData?.agents?.david?.write_allowed_tables || []
                      }
                      onChange={(tables) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  david: {
                                    ...prev.agents.david,
                                    write_allowed_tables: tables,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <ToolPermissionList
                      label="Enabled Tools"
                      idPrefix="david-tool"
                      tools={agentTools?.david ?? EMPTY_AGENT_TOOLS}
                      enabledTools={
                        formData?.agents?.david?.enabled_tools || []
                      }
                      onChange={(tools) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  david: {
                                    ...prev.agents.david,
                                    enabled_tools: tools,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <PromptList
                      label="Suggested Prompts"
                      prompts={formData?.agents?.david?.suggested_prompts || []}
                      onChange={(prompts) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  david: {
                                    ...prev.agents.david,
                                    suggested_prompts: prompts,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                  </div>
                )}
              </div>

              {/* Hanna Agent Section */}
              <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('hanna')}>
                  <div className="flex items-center gap-3">
                    <span className="bg-[linear-gradient(#cacffe,#b2a3fd)] rounded-[50%] overflow-clip">
                      <img src={hannaImage} alt="Hanna" className="w-8 h-8" />
                    </span>
                    <span className="font-semibold text-base text-gray-800">
                      Hanna
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        id="hanna-enabled"
                        checked={formData?.agents?.hanna?.enabled || false}
                        onChange={(e) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  agents: {
                                    ...prev.agents,
                                    hanna: {
                                      ...prev.agents.hanna,
                                      enabled: e.target.checked,
                                    },
                                  },
                                }
                              : prev
                          )
                        }
                        className="h-4 w-4"
                      />
                      <Label htmlFor="hanna-enabled" className="text-sm">
                        Enabled
                      </Label>
                    </div>
                    {expandedSections.hanna ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>
                {expandedSections.hanna && (
                  <div className="space-y-4 pt-4 border-t border-[#e0e7fe]">
                    <TableList
                      label="Read Allowed Tables"
                      tables={
                        formData?.agents?.hanna?.read_allowed_tables || []
                      }
                      onChange={(tables) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  hanna: {
                                    ...prev.agents.hanna,
                                    read_allowed_tables: tables,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <TableList
                      label="Write Allowed Tables"
                      tables={
                        formData?.agents?.hanna?.write_allowed_tables || []
                      }
                      onChange={(tables) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  hanna: {
                                    ...prev.agents.hanna,
                                    write_allowed_tables: tables,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <ToolPermissionList
                      label="Enabled Tools"
                      idPrefix="hanna-tool"
                      tools={agentTools?.hanna ?? EMPTY_AGENT_TOOLS}
                      enabledTools={
                        formData?.agents?.hanna?.enabled_tools || []
                      }
                      onChange={(tools) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  hanna: {
                                    ...prev.agents.hanna,
                                    enabled_tools: tools,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                    <PromptList
                      label="Suggested Prompts"
                      prompts={formData?.agents?.hanna?.suggested_prompts || []}
                      onChange={(prompts) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                agents: {
                                  ...prev.agents,
                                  hanna: {
                                    ...prev.agents.hanna,
                                    suggested_prompts: prompts,
                                  },
                                },
                              }
                            : prev
                        )
                      }
                    />
                  </div>
                )}
              </div>

              {/* Features Section */}
              <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('features')}>
                  <div className="flex items-center gap-3">
                    <span className="text-xl">⚙️</span>
                    <span className="font-semibold text-base text-gray-800">
                      Feature flags
                    </span>
                  </div>
                  {expandedSections.features ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
                {expandedSections.features && (
                  <div className="pt-4 border-t border-[#e0e7fe] space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="meeting-notes"
                        checked={
                          formData?.features?.meeting_notes_table || false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    meeting_notes_table: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="meeting-notes">Meeting Notes Table</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="save-response"
                        checked={formData?.features?.save_response || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    save_response: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="save-response">Save Response</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="content-panel-query-results"
                        checked={
                          formData?.features?.content_panel_query_results ||
                          false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    content_panel_query_results:
                                      checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="content-panel-query-results">
                        Content Panel Query Results
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="data-import"
                        checked={formData?.features?.data_import || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    data_import: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="data-import">Data Import</Label>
                    </div>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="post-call-analysis"
                        aria-describedby="post-call-analysis-description"
                        checked={
                          formData?.features?.post_call_analysis || false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    post_call_analysis: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <div className="space-y-0.5">
                        <Label htmlFor="post-call-analysis">
                          Grade calls on org&apos;s own rubrics
                        </Label>
                        <p
                          id="post-call-analysis-description"
                          className="text-muted-foreground text-xs">
                          Careforce rubrics always grade, even with this off.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="duty-branches"
                        checked={formData?.features?.duty_branches || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    duty_branches: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="duty-branches">Duty Branches</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="snippets"
                        checked={formData?.features?.snippets || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    snippets: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="snippets">Snippets</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="duty-metrics"
                        checked={formData?.features?.duty_metrics || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    duty_metrics: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="duty-metrics">
                        Duty metrics dashboard
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="duty-metrics-renewals"
                        checked={
                          formData?.features?.duty_metrics_renewals || false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    duty_metrics_renewals: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="duty-metrics-renewals">
                        Duty metrics, Renewals (sample data, internal only)
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="duty-access"
                        checked={formData?.features?.duty_access || false}
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    duty_access: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="duty-access">Duty Access Sharing</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="evaluation-rubrics"
                        checked={
                          formData?.features?.evaluation_rubrics || false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    evaluation_rubrics: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="evaluation-rubrics">
                        Evaluation Rubrics
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="test-call-feedback"
                        checked={
                          formData?.features?.test_call_feedback || false
                        }
                        onCheckedChange={(checked) =>
                          setFormData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  features: {
                                    ...prev.features,
                                    test_call_feedback: checked === true,
                                  },
                                }
                              : prev
                          )
                        }
                      />
                      <Label htmlFor="test-call-feedback">
                        Test call feedback
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Organization Instructions Section */}
              <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('orgInstructions')}>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📝</span>
                      <span className="font-semibold text-base text-gray-800">
                        Organization Instructions
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xl">💡</span>
                      <span className="text-[#8e3bd3] text-xs italic">
                        Pro-Tip: Ask David to help you update these instructions
                      </span>
                    </div>
                  </div>
                  {expandedSections.orgInstructions ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
                {expandedSections.orgInstructions && (
                  <div className="space-y-2 pt-4 border-t border-[#e0e7fe]">
                    <textarea
                      id="org-instructions"
                      value={formData?.org_instructions || ''}
                      onChange={(e) =>
                        setFormData((prev) =>
                          prev
                            ? { ...prev, org_instructions: e.target.value }
                            : prev
                        )
                      }
                      placeholder="Custom instructions for all agents in this organization..."
                      rows={6}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e3bd3] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <p className="text-muted-foreground text-xs">
                      These instructions will be added to all agent interactions
                      for this organization.
                    </p>
                  </div>
                )}
              </div>

              {/* The prompt is one text shared by every org, so it appears
                  under careforce — the internal org — rather than beside a
                  customer's settings, where it would read as theirs. */}
              {canManageAllOrgs && selectedOrg === 'careforce' && (
                <AgentPromptSection agent="david" />
              )}

              {/* Calling Section */}
              <div className="space-y-4 border border-[#e0e7fe] rounded-lg p-4 bg-gradient-to-br from-white to-[#fafbff]">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('calling')}>
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📅</span>
                    <div>
                      <span className="font-semibold text-base text-gray-800 block">
                        Calling
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Org default holidays — outbound calls are skipped on
                        these dates unless a duty overrides them
                      </span>
                    </div>
                  </div>
                  {expandedSections.calling ? (
                    <ChevronUp className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  )}
                </div>
                {expandedSections.calling && (
                  <div className="pt-4 border-t border-[#e0e7fe]">
                    <HolidayList
                      label="Holidays"
                      holidays={formData?.calling?.holidays ?? []}
                      onChange={(next) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                calling: { ...prev.calling, holidays: next },
                              }
                            : prev
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Footer Actions */}
      {selectedOrg && !loadingConfig && (
        <div className="border-t p-4 flex justify-between items-center bg-gradient-to-r from-gray-50 to-[#f0f4ff]">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={!hasChanges || updateMutation.isPending}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={
              !hasChanges || updateMutation.isPending || loadingAgentTools
            }
            className="bg-gradient-to-r from-[#8e3bd3] to-[#b2a3fd] hover:from-[#7a32b8] hover:to-[#9d8eea] text-white">
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  )
}
